import { useRef, useState } from "react";
import { Film, Link2, Trash2, Upload, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, web, errMsg } from "../../lib/api";
import { useApi } from "../../hooks/useApi";
import { Panel, Empty, Btn } from "./kit";
import { Field, TextInput } from "./Form";

const MAX = 500 * 1024 * 1024;
const TYPES = ["video/mp4", "video/quicktime", "video/webm", "video/x-m4v"];
const hostedId = (url) => url.match(/\/api\/web\/videos\/([a-f0-9]{32})\/play$/)?.[1] ?? null;
const youtubeId = (url) => url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/))([\w-]{11})/)?.[1] ?? null;
const hostOf = (url) => { try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return "link"; } };
const mb = (b) => `${(b / 1024 / 1024).toFixed(b > 100 * 1024 * 1024 ? 0 : 1)} MB`;
export const linksOf = (p) => (Array.isArray(p?.highlight_links) ? p.highlight_links : []).map((l) => (typeof l === "string" ? l : l?.url)).filter(Boolean);

const HostedTitle = ({ id }) => {
  const { data } = useApi(`/videos/${id}`, web);
  return <>{data?.title ?? "Highlight video"}</>;
};

const Clip = ({ url, i, onRemove }) => {
  const vid = hostedId(url);
  const yt = youtubeId(url);
  return (
    <li data-testid={`highlight-item-${i}`} className="overflow-hidden rounded-2xl border border-[#1E293B] bg-[#0A0E1A]">
      {vid ? (
        <video controls preload="metadata" playsInline src={url} data-testid={`highlight-video-${i}`} className="aspect-video w-full bg-black object-contain" />
      ) : yt ? (
        <iframe title={`Highlight ${i + 1}`} src={`https://www.youtube-nocookie.com/embed/${yt}`} allowFullScreen data-testid={`highlight-youtube-${i}`} className="aspect-video w-full" />
      ) : (
        <a href={url} target="_blank" rel="noreferrer" data-testid={`highlight-link-${i}`} className="flex aspect-video w-full flex-col items-center justify-center gap-2 bg-[#162032] text-[#39FF14] hover:bg-[#1b2940]">
          <ExternalLink className="h-7 w-7" /><span className="text-sm">Watch on {hostOf(url)}</span>
        </a>
      )}
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <p className="min-w-0 truncate text-sm font-semibold text-white">{vid ? <HostedTitle id={vid} /> : yt ? "YouTube highlight" : `Highlight on ${hostOf(url)}`}</p>
        {onRemove && <button data-testid={`highlight-remove-${i}`} onClick={() => onRemove(url)} aria-label="Remove highlight" className="rounded-full p-2 text-slate-400 transition-colors hover:text-red-200"><Trash2 className="h-4 w-4" /></button>}
      </div>
    </li>
  );
};

export const HighlightList = ({ links, onRemove, emptyText }) => (
  !links.length ? <Empty title="No highlights yet" text={emptyText} /> : (
    <ul data-testid="highlight-list" className="grid gap-4 md:grid-cols-2">
      {links.map((url, i) => <Clip key={url} url={url} i={i} onRemove={onRemove} />)}
    </ul>
  )
);

const uploadFile = async (file, title, tid, pid, onProgress) => {
  const { data: s } = await web.post(`/teams/${tid}/players/${pid}/videos`, { title, filename: file.name, size: file.size, content_type: file.type });
  for (let n = 0; n < s.chunks; n += 1) {
    const blob = file.slice(n * s.chunk_size, (n + 1) * s.chunk_size);
    for (let attempt = 0; ; attempt += 1) {
      try {
        await web.put(`/videos/${s.id}/chunks/${n}`, blob, { headers: { "Content-Type": "application/octet-stream" } });
        break;
      } catch (e) {
        if (attempt >= 2 || (e?.response?.status && e.response.status < 500)) throw e;
      }
    }
    onProgress((n + 1) / s.chunks);
  }
  await web.post(`/videos/${s.id}/complete`);
};

const UploadBox = ({ tid, pid, onDone }) => {
  const input = useRef(null);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [progress, setProgress] = useState(null);
  const pick = (f) => {
    if (!f) return;
    if (!TYPES.includes(f.type)) return toast.error("Use an MP4, MOV, M4V or WebM video");
    if (f.size > MAX) return toast.error(`That video is ${mb(f.size)}. The limit is 500 MB.`);
    setFile(f);
    setTitle((t) => t || f.name.replace(/\.[^.]+$/, ""));
  };
  const go = async () => {
    if (!title.trim()) return toast.error("Give the video a title");
    setProgress(0);
    try {
      await uploadFile(file, title.trim(), tid, pid, setProgress);
      toast.success("Video uploaded — it's in the mobile app too");
      setFile(null); setTitle(""); onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setProgress(null);
    }
  };
  const busy = progress !== null;
  return (
    <div className="space-y-3">
      <input ref={input} type="file" accept={TYPES.join(",")} className="hidden" data-testid="highlight-file-input" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
      {!file ? (
        <button type="button" data-testid="highlight-pick-button" onClick={() => input.current?.click()} className="flex w-full flex-col items-center gap-2 rounded-2xl border border-dashed border-[#334155] px-6 py-8 text-slate-300 transition-colors hover:border-[#39FF14] hover:text-white">
          <Upload className="h-6 w-6 text-[#39FF14]" /><span className="font-semibold">Choose a video</span><span className="text-xs text-slate-400">MP4, MOV, M4V or WebM · up to 500 MB</span>
        </button>
      ) : (
        <div className="space-y-3 rounded-2xl border border-[#1E293B] p-4">
          <p className="flex items-center gap-2 text-sm text-slate-300"><Film className="h-4 w-4 text-[#39FF14]" /><span className="truncate">{file.name}</span><span className="shrink-0 text-slate-400">· {mb(file.size)}</span></p>
          <Field label="Title"><TextInput data-testid="highlight-title-input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} disabled={busy} /></Field>
          {busy && (
            <div data-testid="highlight-upload-progress" className="h-2 overflow-hidden rounded-full bg-[#162032]">
              <div className="h-full bg-[#39FF14] transition-[width] duration-300" style={{ width: `${Math.round(progress * 100)}%` }} />
            </div>
          )}
          <div className="flex gap-2">
            <Btn data-testid="highlight-upload-button" disabled={busy} onClick={go}>{busy ? <><Loader2 className="h-4 w-4 animate-spin" />{Math.round(progress * 100)}%</> : <><Upload className="h-4 w-4" />Upload</>}</Btn>
            {!busy && <Btn variant="ghost" data-testid="highlight-cancel-button" onClick={() => setFile(null)}>Cancel</Btn>}
          </div>
          {busy && <p className="text-xs text-slate-400">Keep this page open until the upload finishes.</p>}
        </div>
      )}
    </div>
  );
};

const LinkBox = ({ links, save }) => {
  const [url, setUrl] = useState("");
  const add = async (e) => {
    e.preventDefault();
    const u = url.trim();
    if (!/^https?:\/\/\S+\.\S+/.test(u)) return toast.error("Paste a full link starting with https://");
    if (links.includes(u)) return toast.error("That link is already added");
    if (await save([...links, u], "Highlight link added")) setUrl("");
  };
  return (
    <form onSubmit={add} className="flex flex-col gap-2 sm:flex-row">
      <TextInput data-testid="highlight-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://youtube.com/… or Hudl link" />
      <Btn type="submit" data-testid="highlight-url-add" className="shrink-0"><Link2 className="h-4 w-4" />Add link</Btn>
    </form>
  );
};

export const HighlightManager = ({ tid, pid, player, onChange, playerMode = false }) => {
  const links = linksOf(player);
  const base = `/teams/${tid}/players/${pid}`;
  const save = async (next, msg) => {
    try {
      if (playerMode) await web.post(`${base}/highlight-links`, { url: next[next.length - 1] });
      else await api.patch(base, { highlight_links: next });
      toast.success(msg); onChange(); return true;
    }
    catch (e) { toast.error(errMsg(e)); return false; }
  };
  const remove = async (url) => {
    if (!window.confirm("Remove this highlight from the player's profile?")) return;
    const vid = hostedId(url);
    if (!vid) return save(links.filter((l) => l !== url), "Highlight removed");
    try { await web.delete(`/videos/${vid}`); toast.success("Video removed"); onChange(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-2">
        <Panel><h3 className="mb-4 font-display text-xl font-bold uppercase text-white">Upload a video</h3><UploadBox tid={tid} pid={pid} onDone={onChange} /></Panel>
        <Panel><h3 className="mb-1 font-display text-xl font-bold uppercase text-white">Add a link</h3><p className="mb-4 text-sm text-slate-400">YouTube links play right on the profile. Hudl and other links open in a new tab.</p><LinkBox links={links} save={save} /></Panel>
      </div>
      <HighlightList links={links} onRemove={playerMode ? undefined : remove} emptyText={playerMode ? "Post your first highlight. Your coach and recruiters see it right away." : "Upload a video or add a link. Recruiters and the mobile app see them right away."} />
    </div>
  );
};
